import { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { bumpDataVersion } from '../lib/dataVersion';

const POLL_MS = 2500;      // while a sync runs
const MAX_POLLS = 48;      // ~2 min, then stop nagging the server

/**
 * Follows the LightSpeed sync that the server starts by itself when the data is
 * stale (any data-range call triggers it — see views._maybe_autosync). Opening
 * a page therefore refreshes today's figures without anyone pressing anything.
 *
 * While a sync runs this polls until it finishes, then bumps the data version
 * so every card on the page repaints with the new numbers. Returns the live
 * LightSpeed state: { connected, syncing, last_sync, gaps }.
 */
export function useLiveSync() {
  const [ls, setLs] = useState(null);
  const wasSyncing = useRef(false);

  useEffect(() => {
    let alive = true;
    let timer = null;
    let polls = 0;

    const tick = async () => {
      try {
        const d = await api.dataRange();
        if (!alive) return;
        const next = d?.lightspeed || {};
        setLs(next);
        if (wasSyncing.current && !next.syncing) bumpDataVersion();
        wasSyncing.current = Boolean(next.syncing);
        if (next.syncing && polls < MAX_POLLS) {
          polls += 1;
          timer = setTimeout(tick, POLL_MS);
        }
      } catch {
        // offline or logged out — the page's own error state covers it
      }
    };

    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  return ls;
}

export default useLiveSync;
